// The customer's own FreightRates usage: what the monthly allowance was spent on. Read from the API
// (GET /v1/account/usage, apps/api/src/routes/account.ts), which verifies this session, finds the caller's
// organisation and reads the call log (migration 026). The browser sends only its access token.
import { getAccessToken } from './active-session'

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) || 'https://api.freightratesmcp.com'

export interface UsageDay {
  day: string
  calls: number
  credits: number
  mcp: number
  errors: number
}
export interface UsageRoute {
  route: string
  calls: number
  credits: number
  errors: number
  avg_ms: number | null
}
export interface UsageKey {
  id: string
  name: string | null
  prefix: string | null
  environment: string | null
  revoked: boolean
  last_used_at: string | null
  daily_cap: number | null
  weekly_cap: number | null
  monthly_cap: number | null
  lifetime_cap: number | null
  lifetime_calls: number
  expires_at?: string | null
  /** calls inside the window shown */
  month_calls: number
}
export interface UsageSummary {
  state: 'ok'
  month: string
  /** true when the window is the subscription period (from the purchase), false for a calendar month */
  anchored: boolean
  period_start: string
  period_end: string
  resets_at: string
  /** the paid period ended and was not renewed */
  expired: boolean
  plan: string | null
  plan_label: string
  subscription_status: string | null
  /** in a subscription window the log's count; in a calendar window the number caps are enforced against */
  calls: number
  refused_calls: number
  logged_since: string | null
  logged_calls: number
  credits: number
  by_day: UsageDay[]
  by_route: UsageRoute[]
  by_source: Array<{ source: 'api' | 'mcp'; calls: number }>
  keys: UsageKey[]
}

export type UsageLoad =
  | { state: 'ok'; usage: UsageSummary }
  | { state: 'no_org' }
  | { state: 'signed_out' }
  | { state: 'error' }

/** offset = how many periods back (0 = the current one). */
export async function loadUsage(offset = 0): Promise<UsageLoad> {
  try {
    const token = await getAccessToken()
    if (!token) return { state: 'signed_out' }
    const qs = offset > 0 ? `?offset=${offset}` : ''
    const res = await fetch(`${API_BASE}/v1/account/usage${qs}`, {
      headers: { authorization: `Bearer ${token}` },
    })
    if (res.status === 401) return { state: 'signed_out' }
    if (!res.ok) return { state: 'error' }
    const body = (await res.json()) as { data?: UsageSummary | { state: 'no_org' } }
    if (!body.data) return { state: 'error' }
    if (body.data.state === 'no_org') return { state: 'no_org' }
    return { state: 'ok', usage: body.data as UsageSummary }
  } catch {
    return { state: 'error' }
  }
}

/**
 * The allowance to measure the window against, from the live keys: a monthly cap if one is set, else the
 * lifetime cap of a key (sales issue "500 calls, valid one month" as a lifetime cap with an expiry). Caps are per
 * key, so the largest is shown. Null when nothing is capped.
 */
export function allowance(keys: UsageKey[]): { cap: number; kind: 'monthly' | 'lifetime' } | null {
  const live = keys.filter((k) => !k.revoked)
  const monthly = live.filter((k) => k.monthly_cap != null).map((k) => k.monthly_cap as number)
  if (monthly.length) return { cap: Math.max(...monthly), kind: 'monthly' }
  const lifetime = live.filter((k) => k.lifetime_cap != null).map((k) => k.lifetime_cap as number)
  return lifetime.length ? { cap: Math.max(...lifetime), kind: 'lifetime' } : null
}

/** Friendly names for the route labels the API logs. */
export const ROUTE_NAMES: Record<string, string> = {
  '/': 'Freight indices',
  '/indices': 'Freight indices',
  '/lane': 'Lane rate',
  '/lane/cards': 'Lane sailings',
  '/lane/history': 'Lane history',
  '/lane/spot': 'Spot rates (FCL, LCL)',
  '/v1/rates/quote': 'Live spot search',
  '/v1/rates/book': 'Booking contacts',
  '/v1/rates/providers': 'Rate providers',
  '/v1/rates/providers/:id': 'Rate providers',
  other: 'Other',
}

/** Every UTC day from the window start up to its end (or today, for the running window), quiet days as zeros. */
export function fillDays(
  startIso: string,
  endIso: string,
  days: UsageDay[],
  today = new Date()
): UsageDay[] {
  const day = (d: Date) => d.toISOString().slice(0, 10)
  const start = Date.parse(`${startIso.slice(0, 10)}T00:00:00Z`)
  const lastDay = Math.min(
    Date.parse(`${endIso.slice(0, 10)}T00:00:00Z`),
    Date.parse(`${day(today)}T00:00:00Z`)
  )
  const by = new Map(days.map((d) => [d.day, d]))
  const out: UsageDay[] = []
  for (let t = start; t <= lastDay && out.length < 400; t += 86_400_000) {
    const key = day(new Date(t))
    out.push(by.get(key) ?? { day: key, calls: 0, credits: 0, mcp: 0, errors: 0 })
  }
  return out
}
